import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { Button, Table, Typography } from "antd";
import Title from "antd/es/typography/Title";

import usePageLoading from "hooks/usePageLoading";

import {
  CardGridSkeleton,
  PageLoadingContainer,
  SkeletonBlock,
  SkeletonHeading,
  SkeletonText,
  SlowStateBanner,
  TableSkeleton,
} from "js/components/Loading";
import IconCheck from "js/components/Memberships/Plans/IconCheck";
import IconClose from "js/components/Memberships/Plans/IconClose";
import { Button as RetryButton } from "js/components/ui/button";

import { Plan } from "js/pages/Memberships/types";

import { getMembershipPlans } from "../../../api/memberships";

const { Text } = Typography;

const MembershipPlans: React.FC = () => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [currentPlanName, setCurrentPlanName] = useState<string>("");
  const [freePlan, setFreePlan] = useState<Plan>({
    id: "",
    name: "free",
    price: 0,
    user_members: 0,
  });
  const [proPlan, setProPlan] = useState<Plan>({
    id: "",
    name: "pro",
    price: 0,
    user_members: 0,
  });
  const [elitePlan, setElitePlan] = useState<Plan>({
    id: "",
    name: "elite",
    price: 0,
    user_members: 0,
  });

  const navigate = useNavigate();

  const { membershipData, loading: membershipLoading } = useSelector(
    (state) => state.entities.membership,
  );
  const [isPlansLoading, setIsPlansLoading] = useState(true);

  const columns = [
    {
      title: "",
      dataIndex: "plan",
      key: "plan",
      render: (text: string) => <Text strong>{text}</Text>,
      width: "40%",
    },
    ...plans.map((plan) => ({
      title: (
        <>
          <Title level={4}>{plan.name}</Title>
          {plan.name !== "Free" && (
            <Text mark color={"#ff0000"}>
              * Pruébalo por 30 días gratis
            </Text>
          )}
        </>
      ),
      dataIndex: plan.name,
      key: plan.name,
      align: "center",
    })),
  ];
  const data = [
    {
      key: "0",
      plan: "",
      [freePlan.name]: (
        <>
          <Text strong>${freePlan.price}</Text>
          <Text type={"secondary"}>/mes</Text>
        </>
      ),
      [proPlan.name]: (
        <>
          <Text strong>${proPlan.price}</Text>
          <Text type={"secondary"}>/mes</Text>
        </>
      ),
      [elitePlan.name]: (
        <>
          <Text strong>${elitePlan.price}</Text>
          <Text type={"secondary"}>/mes</Text>
        </>
      ),
    },
    {
      key: "1",
      plan: "Número de usuarios",
      [freePlan.name]: "1 usuario",
      [proPlan.name]: "3 usuarios",
      [elitePlan.name]: "10 usuarios",
    },
    // {
    //   key: "2",
    //   plan: "Usuario adicional",
    //   [freePlan.name]: "-",
    //   [proPlan.name]: "$ 99",
    //   [elitePlan.name]: "$ 99",
    // },
    {
      key: "3",
      plan: "CRM de inmuebles e inquilinos",
      [freePlan.name]: <IconCheck />,
      [proPlan.name]: <IconCheck />,
      [elitePlan.name]: <IconCheck />,
    },
    {
      key: "4",
      plan: "Solicitud de investigación personalizada",
      [freePlan.name]: <IconCheck />,
      [proPlan.name]: <IconCheck />,
      [elitePlan.name]: <IconCheck />,
    },
    {
      key: "5",
      plan: "Progreso del inquilino en tiempo real",
      [freePlan.name]: <IconCheck />,
      [proPlan.name]: <IconCheck />,
      [elitePlan.name]: <IconCheck />,
    },
    {
      key: "6",
      plan: "Resguardo y consulta de expedientes",
      [freePlan.name]: <IconCheck />,
      [proPlan.name]: <IconCheck />,
      [elitePlan.name]: <IconCheck />,
    },
    {
      key: "7",
      plan: "Multireporte: la investigación más completa y segura en México",
      [freePlan.name]: <IconCheck />,
      [proPlan.name]: <IconCheck />,
      [elitePlan.name]: <IconCheck />,
    },
    {
      key: "8",
      plan: "Reporte Buró de Crédito",
      [freePlan.name]: <IconCheck />,
      [proPlan.name]: <IconCheck />,
      [elitePlan.name]: <IconCheck />,
    },
    {
      key: "9",
      plan: "Documentos del inquilino",
      [freePlan.name]: <IconCheck />,
      [proPlan.name]: <IconCheck />,
      [elitePlan.name]: <IconCheck />,
    },
    {
      key: "10",
      plan: "Soporte vía whatsapp para inquilino y asesor",
      [freePlan.name]: <IconCheck />,
      [proPlan.name]: <IconCheck />,
      [elitePlan.name]: <IconCheck />,
    },
    {
      key: "11",
      plan: "Tutoriales en video",
      [freePlan.name]: <IconCheck />,
      [proPlan.name]: <IconCheck />,
      [elitePlan.name]: <IconCheck />,
    },
    {
      key: "12",
      plan: "Monitorea y gestiona las investigaciones tus asesores",
      [freePlan.name]: <IconClose />,
      [proPlan.name]: <IconCheck />,
      [elitePlan.name]: <IconCheck />,
    },
    {
      key: "13",
      plan: "Datos seguros: al remover a un asesor su información permanece en tu cuenta",
      [freePlan.name]: <IconClose />,
      [proPlan.name]: <IconCheck />,
      [elitePlan.name]: <IconCheck />,
    },
    {
      key: "14",
      plan: "Personaliza el Multireporte con tu logo",
      [freePlan.name]: <IconClose />,
      [proPlan.name]: <IconClose />,
      [elitePlan.name]: <IconCheck />,
    },
    {
      key: "15",
      plan: "Descuento en paquetes de investigaciones",
      [freePlan.name]: <IconClose />,
      [proPlan.name]: <Text strong> 5%</Text>,
      [elitePlan.name]: <Text strong> 10%</Text>,
    },
    {
      key: "16",
      plan: "",
      [freePlan.name]:
        currentPlanName == freePlan.id ? (
          <Button ghost type={"primary"} onClick={() => navigate("/")}>
            Continuar en [freePlan.name]
          </Button>
        ) : (
          ""
        ),
      [proPlan.name]: (
        <Button
          ghost
          type={"primary"}
          disabled={currentPlanName == proPlan.name}
          onClick={() => navigate(`/memberships/checkout?plan=${proPlan.id}`)}
        >
          {currentPlanName == proPlan.name ? "Plan Actual" : "Contratar"}
        </Button>
      ),
      [elitePlan.name]: (
        <Button
          ghost
          type={"primary"}
          disabled={currentPlanName == elitePlan.name}
          onClick={() => navigate(`/memberships/checkout?plan=${elitePlan.id}`)}
        >
          {currentPlanName == elitePlan.name ? "Plan Actual" : "Contratar"}
        </Button>
      ),
    },
  ];

  const fetchPlans = useCallback(async () => {
    setIsPlansLoading(true);
    try {
      const response = await getMembershipPlans();
      const sortedPlans = [...response].sort(
        (a: Plan, b: Plan) => a.price - b.price,
      );
      setPlans(sortedPlans);
      setFreePlan(
        sortedPlans[0] ?? {
          id: "",
          name: "free",
          price: 0,
          user_members: 0,
        },
      );
      setProPlan(
        sortedPlans[1] ?? {
          id: "",
          name: "pro",
          price: 0,
          user_members: 0,
        },
      );
      setElitePlan(
        sortedPlans[2] ?? {
          id: "",
          name: "elite",
          price: 0,
          user_members: 0,
        },
      );
    } catch (error) {
      console.error("Error fetching membership plans:", error);
      setPlans([]);
    } finally {
      setIsPlansLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!membershipData) {
      return;
    }
    void fetchPlans();
    setCurrentPlanName(membershipData.plan);
  }, [fetchPlans, membershipData]);

  const { showSkeleton, showSlowState, dismissSlowState } = usePageLoading({
    isLoading: membershipLoading || isPlansLoading,
  });

  const handleRetryFetch = () => {
    dismissSlowState();
    void fetchPlans();
  };

  const slowStateBanner = showSlowState ? (
    <SlowStateBanner
      actions={
        <RetryButton
          size="sm"
          variant="outline"
          className="border-amber-200 bg-amber-50/70 text-amber-700 hover:bg-amber-50"
          onClick={handleRetryFetch}
        >
          Reintentar
        </RetryButton>
      }
    >
      <p className="font-medium">Esto está tardando más de lo previsto.</p>
      <p>Verifica tu conexión o vuelve a intentarlo en unos segundos.</p>
    </SlowStateBanner>
  ) : null;

  const headerSkeleton = (
    <div className="space-y-3">
      <SkeletonHeading className="h-8 w-32" />
    </div>
  );

  const contentSkeleton = (
    <div className="space-y-6">
      <TableSkeleton columns={4} rows={17} showFooter={false} />
    </div>
  );

  return (
    <PageLoadingContainer
      showSkeleton={showSkeleton}
      slowState={slowStateBanner}
      headerSkeleton={headerSkeleton}
      contentSkeleton={contentSkeleton}
      className="space-y-6"
    >
      <div>
        <Title level={2}>Planes</Title>
        <Table
          style={{ overflowX: "auto" }}
          scroll={{ x: "max-content" }}
          size={"small"}
          loading={isPlansLoading && !showSkeleton}
          dataSource={data}
          columns={columns}
          pagination={false}
        />
      </div>
    </PageLoadingContainer>
  );
};
export default MembershipPlans;
